import {
    type StatusOption,
    StatusPicker,
} from '@apps/web/components/ui/status_picker';
import { cn } from '@apps/web/lib/utils';
import { type CaregiverResponseType } from '@libraries/typescript/api_client';

// The six outreach response statuses, shared by every place a caregiver's
// availability is shown or edited. Dot color drives the dropdown rows; the
// pill colors below drive the trigger.
const RESPONSE_OPTIONS: ReadonlyArray<StatusOption<CaregiverResponseType>> = [
    {
        value: 'available_interested',
        label: 'Available',
        dotColor: 'bg-green-500',
    },
    {
        value: 'not_a_fit',
        label: 'Not a fit',
        dotColor: 'bg-yellow-500',
    },
    { value: 'unavailable', label: 'Unavailable', dotColor: 'bg-red-500' },
    { value: 'pending', label: 'Pending Response', dotColor: 'bg-slate-400' },
    { value: 'no_response', label: 'No Response', dotColor: 'bg-slate-400' },
    { value: 'unknown', label: 'Needs Review', dotColor: 'bg-amber-500' },
];

const OPTION_BY_VALUE = new Map(
    RESPONSE_OPTIONS.map((option) => [option.value, option] as const),
);
OPTION_BY_VALUE.set('available_not_interested', {
    value: 'available_not_interested',
    label: 'Not a fit',
    dotColor: 'bg-yellow-500',
});

type PillColors = {
    readonly background: string;
    readonly text: string;
    readonly hover: string;
};

const PILL_COLORS: Record<CaregiverResponseType, PillColors> = {
    available_interested: {
        background: 'bg-green-100 dark:bg-green-950/50',
        text: 'text-green-800 dark:text-green-300',
        hover: 'hover:bg-green-200 dark:hover:bg-green-900/60',
    },
    available_not_interested: {
        background: 'bg-yellow-100 dark:bg-yellow-950/50',
        text: 'text-yellow-800 dark:text-yellow-300',
        hover: 'hover:bg-yellow-200 dark:hover:bg-yellow-900/60',
    },
    not_a_fit: {
        background: 'bg-yellow-100 dark:bg-yellow-950/50',
        text: 'text-yellow-800 dark:text-yellow-300',
        hover: 'hover:bg-yellow-200 dark:hover:bg-yellow-900/60',
    },
    unavailable: {
        background: 'bg-red-100 dark:bg-red-950/50',
        text: 'text-red-800 dark:text-red-300',
        hover: 'hover:bg-red-200 dark:hover:bg-red-900/60',
    },
    pending: {
        background: 'bg-muted',
        text: 'text-muted-foreground',
        hover: 'hover:bg-accent',
    },
    no_response: {
        background: 'bg-muted',
        text: 'text-muted-foreground',
        hover: 'hover:bg-accent',
    },
    unknown: {
        background: 'bg-amber-100 dark:bg-amber-950/50',
        text: 'text-amber-800 dark:text-amber-300',
        hover: 'hover:bg-amber-200 dark:hover:bg-amber-900/60',
    },
};

type ResponsePickerProps = {
    readonly value: CaregiverResponseType | null;
    readonly onSelect?: (value: CaregiverResponseType) => void;
    readonly disabled?: boolean;
    readonly ariaLabel?: string;
    // When `value` is null, render this option's pill as a display default
    // (not marked selected). Used where an empty response reads as "Pending".
    readonly defaultDisplayValue?: CaregiverResponseType;
    // Optional three-way editing: clicking the recorded value resets to it,
    // clicking the active value clears it. When these are omitted the picker is
    // a plain single-select.
    readonly recordedValue?: CaregiverResponseType | null;
    readonly onReset?: () => void;
    readonly onClear?: () => void;
};

/**
 * The single outreach response status control. Renders a colored status pill and
 * a searchable dropdown of the six response options. Supports both plain
 * selection and three-way (select / reset-to-recorded / clear) editing.
 */
export function ResponsePicker({
    value,
    onSelect,
    disabled = false,
    ariaLabel,
    defaultDisplayValue,
    recordedValue,
    onReset,
    onClear,
}: ResponsePickerProps) {
    const displayValueKey = value ?? defaultDisplayValue ?? null;
    const displayOption = displayValueKey
        ? (OPTION_BY_VALUE.get(displayValueKey) ?? null)
        : null;

    const triggerClassName = displayOption
        ? cn(
              'rounded-full border px-2 py-0.5',
              PILL_COLORS[displayOption.value].background,
              PILL_COLORS[displayOption.value].text,
              PILL_COLORS[displayOption.value].hover,
          )
        : 'rounded-full border px-2 py-0.5 text-muted-foreground hover:bg-muted';

    const displayNode = displayOption ? (
        <span className="inline-flex items-center gap-1.5">
            <span
                className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    displayOption.dotColor,
                )}
            />
            <span>{displayOption.label}</span>
        </span>
    ) : undefined;

    const handleSelect = (selected: CaregiverResponseType) => {
        if (onClear && value === selected) {
            onClear();
            return;
        }
        if (onReset && recordedValue === selected) {
            onReset();
            return;
        }
        onSelect?.(selected);
    };

    return (
        <StatusPicker
            options={RESPONSE_OPTIONS}
            value={value}
            onSelect={handleSelect}
            disabled={disabled}
            displayValue={displayNode}
            placeholder="—"
            triggerClassName={triggerClassName}
            contentClassName="w-[200px]"
            contentSide="left"
            searchable={true}
            searchPlaceholder="Search status..."
            ariaLabel={ariaLabel}
        />
    );
}
